import { useTTSQuery } from "@src/data/queries/tts";
import { useState } from "react";
import { IconContext } from "react-icons";
import { FaMicrophone } from "react-icons/fa";
import { toast } from "react-toastify";

import { Button } from "@src/components/Button";
import { Checkbox } from "@src/components/Checkbox";
import { FormGroup } from "@src/components/FormGroup";
import { Loader } from "@src/components/Loader";
import { Modal } from "@src/components/Modal";
import { MultiplierControl } from "@src/components/MultiplierControl";
import { Select } from "@src/components/Select";
import { Textarea } from "@src/components/Textarea";

import { envName } from "@src/env";

import { playerOverlays } from "@src/services/playerOverlays";
import { ttsProviders } from "@src/services/tts";
import { toSameOriginPath } from "@src/utils/toSameOriginPath";

import "./style.less";
import type { TTSProps } from "./types";

export function TTS({ radio }: TTSProps) {
  const { name, initialSpeed } =
    ttsProviders[radio.tts] || {
      name: "",
      initialSpeed: 1.0,
    };

  // Vozes vêm 100% da API (radio.tts_voices), fonte única da verdade — o front
  // não conhece IDs nem nomes de voz. Sempre traz ≥1 voz; `?? []` cobre apenas
  // a janela transitória de um radio persistido antigo antes do refetch.
  const displayVoices = radio.tts_voices ?? [];

  const [isOpen, setIsOpen] = useState(false);

  const [beep, setBeep] = useState(false);
  const [speed, setSpeed] = useState(initialSpeed);
  const [voice, setVoice] = useState(displayVoices[0]?.id);
  const [text, setText] = useState("");
  const tts = useTTSQuery(name, voice, text);
  const [loading, setLoading] = useState(false);
  const _loading = tts.isLoading || loading;

  return (
    <>
      <IconContext.Provider
        value={{ color: "#ffffff", className: "icon", size: "1.5em" }}
      >
        <div
          className="TTS-component clickable"
          onClick={() => setIsOpen(true)}
          data-tooltip-id="footer"
          data-tooltip-content={"Locutor Virtual\nTexto Para Áudio (TTS)"}
          data-tooltip-class-name="text-center"
          data-tooltip-place="top"
        >
          <FaMicrophone />
        </div>
      </IconContext.Provider>
      <Modal className="TTS-modal" isOpen={isOpen} setIsOpen={setIsOpen}>
        <h2>Locutor Virtual</h2>
        <hr />
        {envName === "dev:sw2" && (
          <p className="dev-warning">
            Lembre-se de executar <code>bun run dev</code> no projeto
            tts.fabriccamultimidia.com.br para que{" "}
            <code>http://127.0.0.1:3001</code> fique disponível.
          </p>
        )}
        <form
          onSubmit={(event) => {
            event.preventDefault();
            // A fila de overlays é global: bloqueia enquanto qualquer overlay
            // (outro TTS, agendado, sob demanda) estiver tocando.
            if (playerOverlays.busy) {
              toast.warn("Aguarde o áudio atual terminar.");
              return;
            }
            setLoading(true);
            tts.refetch().then(
              ({ data }) => {
                if (!data) {
                  toast.error("Falha ao gerar áudio TTS (Erro 1)");
                  setLoading(false);
                  return;
                }
                setText("");
                playerOverlays
                  .speakTTS({
                    idRadio: radio.name,
                    url: data,
                    speed,
                    beepUrl:
                      radio.tts_beep && beep
                        ? toSameOriginPath(radio.tts_beep)
                        : null,
                    onStart: () => {
                      toast.success("Áudio TTS gerado com sucesso.");
                      setLoading(false);
                      setTimeout(() => {
                        setIsOpen(false);
                      }, 300);
                    },
                  })
                  .catch((error) => {
                    console.log("Falha ao reproduzir áudio TTS:", error);
                    toast.error("Falha ao reproduzir o áudio TTS");
                    setLoading(false);
                  });
              },
              (error) => {
                console.log("Falha ao gerar áudio TTS:", error);
                toast.error("Falha ao gerar áudio TTS (Erro 2)");
                setLoading(false);
              },
            );
          }}
        >
          <fieldset disabled={_loading}>
            <FormGroup>
              <label htmlFor="voice">Voz</label>
              <Select
                id="voice"
                name="voice"
                required
                value={voice}
                onChange={(event) => setVoice(event.target.value)}
                title="Voz"
              >
                {displayVoices.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <MultiplierControl
              label="Velocidade"
              initialValue={initialSpeed}
              value={speed}
              setValue={setSpeed}
              min={0.75}
              max={1.75}
              step={0.05}
            />

            <FormGroup>
              <label htmlFor="text">Texto</label>
              <Textarea
                id="text"
                name="text"
                required
                value={text}
                onChange={(event) => setText(event.target.value)}
                placeholder="Digite o texto a ser falado"
              />
            </FormGroup>

            {radio.tts_beep && (
              <label className="beep">
                <Checkbox
                  name="beep"
                  checked={beep}
                  onChange={(event) => setBeep(event.target.checked)}
                />
                Tocar beep antes da fala
              </label>
            )}

            <Button type="submit" disabled={_loading}>
              Falar
            </Button>
            {tts.isError && <span className="error">{tts.error.message}</span>}
            <Loader visible={_loading} />
          </fieldset>
        </form>
      </Modal>
    </>
  );
}
